'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { loadConsent, migrateLegacyThemeFromLocalStorage, saveConsent } from './consent';
import type { CookieConsentState } from './types';

type CookieConsentContextValue = {
  consent: CookieConsentState | null;
  isReady: boolean;
  preferencesOpen: boolean;
  acceptAll: () => void;
  rejectNonEssential: () => void;
  updatePreferences: (choices: Pick<CookieConsentState, 'analytics'>) => void;
  openPreferences: () => void;
  closePreferences: () => void;
};

const CookieConsentContext = createContext<CookieConsentContextValue | null>(null);

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  // Always boot identical on SSR and first client paint — reading window/cookies
  // in useState() causes React #418 (hydration mismatch) and breaks sign-in UI.
  const [consent, setConsent] = useState<CookieConsentState | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  useEffect(() => {
    migrateLegacyThemeFromLocalStorage();
    setConsent(loadConsent());
    setIsReady(true);
  }, []);

  const acceptAll = useCallback(() => {
    const next = saveConsent({ analytics: true });
    setConsent(next);
    setPreferencesOpen(false);
  }, []);

  const rejectNonEssential = useCallback(() => {
    const next = saveConsent({ analytics: false });
    setConsent(next);
    setPreferencesOpen(false);
  }, []);

  const updatePreferences = useCallback((choices: Pick<CookieConsentState, 'analytics'>) => {
    const next = saveConsent(choices);
    setConsent(next);
    setPreferencesOpen(false);
  }, []);

  const openPreferences = useCallback(() => setPreferencesOpen(true), []);
  const closePreferences = useCallback(() => setPreferencesOpen(false), []);

  const value = useMemo(
    () => ({
      consent,
      isReady,
      preferencesOpen,
      acceptAll,
      rejectNonEssential,
      updatePreferences,
      openPreferences,
      closePreferences,
    }),
    [
      consent,
      isReady,
      preferencesOpen,
      acceptAll,
      rejectNonEssential,
      updatePreferences,
      openPreferences,
      closePreferences,
    ],
  );

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
}

export function useCookieConsent(): CookieConsentContextValue {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) {
    throw new Error('useCookieConsent must be used within CookieConsentProvider');
  }
  return ctx;
}
